import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Flex, IconAsButton, SimpleButton, Textinput, Typo, theme } from '../../../../ui';
import { media } from '../../../../utils/media';
import { usePage, useGetPageByIdQuery } from '../../hooks';
import { useNotesActions } from '../../../notes/hooks/useNotesActions';
import { getDarkerThemeColor } from '../../../../ui/theme/colors';

const NotesItem = styled(Flex)`
  padding:6px 4px 6px 12px;

  &:hover {
    background-color:${theme.colors.NEU_50};
  }
`

const Container = styled.div`
  padding: 1rem 0 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${theme.colors.NEU_0};
  border-radius: 15px;
  overflow: hidden;
  height: calc(100vh - ${theme.headerSize});

  ${media.lessThan('bigTablet')} {
    min-height: ${({ $hasNotes }) => ($hasNotes ? '250px' : '150px')};
    max-height: ${({ $hasNotes }) => ($hasNotes ? '80vh' : 'auto')};
    height: auto;
    overflow: hidden;
    padding: 0.5rem 0 0;
  }

  ${media.lessThan('phablet')} {
    max-height: ${({ $hasNotes }) => ($hasNotes ? '70vh' : 'auto')};
  }
`;

const TitleContainer = styled.div`
  flex-shrink: 0;
  padding: 6px 20px 6px;
  background: ${theme.colors.NEU_0};
  z-index: 20;

  ${media.lessThan('phablet')} {
    padding: 5px 20px 5px;
  }
`;

const NotesList = styled.ul`
  margin: 6px;
  padding: 0;
  flex-grow: 1;
  overflow-y: auto;

  ${media.lessThan('bigTablet')} {
    max-height: 40vh;
  }

  ${media.lessThan('phablet')} {
    max-height: 35vh;
  }
`;

const InputContainer = styled.div`
  padding: 1rem;
  text-align: right;
  background-color: ${theme.colors.NEU_0};
`;

const NotesContent = ({themeColor}) => {
  const { t } = useTranslation();
  const page = usePage();
  // RTKQ will automatically refetch when page.id changes
  const { isFetching } = useGetPageByIdQuery(
    { pageId: page?.id },
    { skip: !page?.id }
  );
  const notesRef = useRef(null);
  const editNotesRef = useRef(null);
  const [notesForPage, setNotesForPage] = useState([]);
  const [editingNote, setEditingNote] = useState();

  const { createNote, updateNote, saveNotes, deleteNote } = useNotesActions();

  useEffect(() => {
    if (page?.pageRows?.length) {
      let notes = [...page.notes];
      page.pageRows.forEach((row) => {
        row.pageModules.forEach((module) => {
          const note = {...module.note, type:'module', id:module.id}
          if (module.note.text) notes.push(note);
        });
      });
      // Merge with any temporary notes that haven't been replaced yet
      setNotesForPage(prevNotes => {
        const tempNotes = prevNotes.filter(note =>
          typeof note.id === 'string' && note.id.startsWith('temp_')
        );

        // Remove any temp notes that might have been replaced by real notes
        const filteredTempNotes = tempNotes.filter(tempNote =>
          !notes.some(realNote => realNote.text === tempNote.text)
        );

        return [...notes, ...filteredTempNotes];
      });
    }
  }, [page]);

  const onSave = async () => {
    if (notesRef.current?.value.trim()) {
      const noteText = notesRef.current.value;
      const result = await createNote({ pageId: page.id, text: notesRef.current.value });
      if (result) {
        const tempNote = {
          id: result?.id || `temp_${Date.now()}`,
          text: noteText,
        };
        setNotesForPage(prevNotes => [...prevNotes, tempNote]);

        // when we start having an id from the payload we can use it instead of Date.now()
        notesRef.current.value = '';
      }
    }
  };

  const onUpdate = async () => {
    if (editNotesRef.current?.value.trim() && editingNote) {
      const text = editNotesRef.current.value;
      const result = await updateNote({ noteId: editingNote.id, pageId: page.id, text });
      if (result === true) {
        setNotesForPage(prevNotes =>
          prevNotes.map(note =>
            note.id === editingNote.id ? { ...note, text: text } : note
          )
        );
        setEditingNote(null);
      }
    }
  };

  const onDelete = async (note) => {
    if(!!note.type && note.type ==='module') {
      await saveNotes({ pageModuleId: note.id, text: '', pageId: page.id });
    }
    else {
      await deleteNote({ pageId: page.id, noteId: note.id });
    }
    setNotesForPage(prevNotes => prevNotes.filter(n => n.id !== note.id));
  };

  const onEditNote = (note) => {
    setEditingNote(note);
  }

  return (
    <Container $hasNotes={notesForPage.length > 0}>
      <TitleContainer>
        <Typo.ContentBigHeader>Your notes</Typo.ContentBigHeader>
      </TitleContainer>
      <NotesList $hasNotes={notesForPage.length > 0}>
        {notesForPage.map((note, index) => (
          editingNote && editingNote.id === note.id ?
          <NotesItem key={index} align='flex-end' gap='5'>
            <Textinput
              inputRef={editNotesRef}
              defaultValue={note.text}
              label={t('coursePage.notes.editNote')}
              isMultiline
              minRows={3}
              endIcon={
                <IconAsButton size={'x-small'} iconName={'Save'} clickHandle={() => {
                  onUpdate();
                  setEditingNote(null);
                }} />
              }
            />
          </NotesItem>
          : <NotesItem key={index} align='flex-end' gap='5'>
            <Typo.Text>{note.text}</Typo.Text>
            <SimpleButton
              variant='ghost'
              icon='Edit'
              size='x-small'
              disabled={isFetching}
              onClick={()=> onEditNote(note)}
              aria-label="Edit note"
              bgColor={getDarkerThemeColor(themeColor)}
            />
            <SimpleButton
              variant='ghost'
              noTooltip={true}
              bgColor={theme.colors.ACTION}
              icon='Delete' size='x-small'
              disabled={isFetching}
              onClick={()=> onDelete(note)}
              aria-label="delete this note"
            />
          </NotesItem>
        ))}
      </NotesList>
      <InputContainer>
        <Textinput
          key={'take-a-note'}
          inputRef={notesRef}
          label={t('coursePage.notes.writeNote')}
          isMultiline
          minRows={3}
          endIcon={
            <IconAsButton size={'x-small'} iconName={'Send'} clickHandle={onSave} />
          }
        />
      </InputContainer>
    </Container>
  );
};

export default NotesContent;
